import { buildOnlineInvitePath } from './appLinks';
import { isValidGroupToken, normalizeGroupToken } from './groupToken';
import { clearGroupContext, forgetGroupLastGame, loadGroupToken } from './groupPersistence';
import { clearSession, readSession, type SessionPhase, type SessionState } from './sessionContract';

const ACTIVE_ONLINE_PHASES = new Set<SessionPhase>([
  'order-reveal',
  'in-game',
]);

function sanitizeGroupToken(raw: string | null | undefined): string | null {
  const normalized = normalizeGroupToken(raw);
  if (!normalized || !isValidGroupToken(normalized)) return null;
  return normalized;
}

function normalizeGameId(raw: string | null | undefined): string | null {
  const trimmed = (raw || '').trim().toUpperCase();
  return trimmed || null;
}

export function resolveOnlineAutoResumePath(
  session: SessionState | null,
  groupToken: string | null | undefined,
): string | null {
  if (session?.mode !== 'ONLINE') return null;
  if (!ACTIVE_ONLINE_PHASES.has(session.phase)) return null;

  const normalizedRoomId = (session.gameId || '').trim().toUpperCase();
  const normalizedGroupToken = sanitizeGroupToken(groupToken)
    || sanitizeGroupToken(session.extras?.groupToken);
  const hasRememberedPlayer = typeof session.extras?.lastPlayerId === 'string' && session.extras.lastPlayerId.trim().length > 0;

  if (normalizedRoomId && session.phase === 'in-game' && hasRememberedPlayer) {
    return `/online/game/${encodeURIComponent(normalizedRoomId)}`;
  }

  if (normalizedGroupToken) {
    return buildOnlineInvitePath(normalizedGroupToken);
  }

  if (normalizedRoomId) {
    return `/online?game=${encodeURIComponent(normalizedRoomId)}`;
  }

  if (typeof session.lastPath === 'string' && session.lastPath.startsWith('/online')) {
    return session.lastPath;
  }

  return '/online';
}

export function clearStaleOnlineAutoResume(
  gameId: string | null | undefined,
  preferredGroupToken?: string | null,
): string | null {
  const normalizedRoomId = normalizeGameId(gameId);
  if (!normalizedRoomId || typeof localStorage === 'undefined') return null;

  const session = readSession();
  const sessionMatches = session?.mode === 'ONLINE'
    && normalizeGameId(session.gameId) === normalizedRoomId;

  const lastOnlineGameId = (() => {
    try {
      return localStorage.getItem('dice1k_last_online_game');
    } catch {
      return null;
    }
  })();
  const lastOnlineGameMatches = normalizeGameId(lastOnlineGameId) === normalizedRoomId;

  const groupLastGameMatches = normalizeGameId(localStorage.getItem('dice1k_group_last_game')) === normalizedRoomId;

  if (!sessionMatches && !lastOnlineGameMatches && !groupLastGameMatches) {
    return null;
  }

  if (sessionMatches) {
    clearSession();
  }

  if (lastOnlineGameMatches) {
    try {
      localStorage.removeItem('dice1k_last_online_game');
    } catch {
      // ignore persistence issues
    }
  }

  if (groupLastGameMatches) {
    forgetGroupLastGame(normalizedRoomId);
  }

  const fallbackGroupToken = sanitizeGroupToken(preferredGroupToken)
    || sanitizeGroupToken(session?.extras?.groupToken)
    || sanitizeGroupToken(loadGroupToken());

  if (!fallbackGroupToken) {
    clearGroupContext();
    return '/online';
  }

  return buildOnlineInvitePath(fallbackGroupToken);
}